import React from 'react';
import {
  Stack,
  Text,
  Input,
  Select,
  Checkbox,
  Button,
  ControlLink,
  Overlay,
  SelectableOption,
  Clickable,
  LoadingBalls
} from '@moda/om';
import ExitIcon from '@moda/icons/exit-16';
import { hrefFor } from '../../routers';
import { tc } from '../../lib/trackingContext';
import { useModaNetworkForm } from './useModaNetworkForm';
import './ModaNetworkForm.scss';

type Props = { onSuccess: () => void; onClose: () => void };

const BUSINESS_TYPE_OPTIONS: SelectableOption[] = [
  {
    label: 'Personal Stylist',
    value: 'Personal Stylist'
  },
  {
    label: 'Celebrity Stylist',
    value: 'Celebrity Stylist'
  },
  {
    label: 'Costume Designer',
    value: 'Costume Designer'
  },
  {
    label: 'Interior Designer',
    value: 'Interior Designer'
  },
  {
    label: 'Bridal Stylist',
    value: 'Bridal Stylist'
  },
  {
    label: 'Other',
    value: 'Other'
  }
];

export const ModaNetworkForm: React.FC<Props> = ({ onSuccess, onClose }) => {
  const { handleSubmit, formErrors, error, loading } = useModaNetworkForm({ onSuccess });

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit(event);
    tc.track('Moda Network Form Submit Attempted', {
      addToTrackingEvent: {
        label: 'Submit',
        location: 'Moda Network Form'
      }
    });
  };

  const handleModalClose = () => {
    onClose();
    tc.track('Moda Network Form Closed', {
      addToTrackingEvent: {
        label: 'X',
        location: 'Moda Network Form Close button'
      }
    });
  };

  return (
    <form className="ModaNetworkForm" onSubmit={handleFormSubmit}>
      <Clickable className="ModaNetworkForm__close" aria-label="Close" onClick={handleModalClose}>
        <ExitIcon />
      </Clickable>
      <Stack space={5}>
        <Stack space={4}>
          <Text className="ModaNetworkForm__title" treatment="h3" family="serif">
            Apply Now
          </Text>
          <Text className="ModaNetworkForm__description">
            MODA Network provides a unique space for Stylists & Costume Designers to grow their
            business. Provide your detailed profile below and our team will review and be in touch
            soon.
          </Text>
          <Input label="Full Name" name="name" error={formErrors.name?.message} />
          <Input label="Email" name="email" error={formErrors.email?.message} />
          <Input label="Business type" name="business" error={formErrors.business?.message}>
            <Select label="" options={BUSINESS_TYPE_OPTIONS} />
          </Input>

          <Input label="Phone Number" name="phone" error={formErrors.phone?.message} />
          <Input
            label="Website or Social Media Link"
            name="link"
            error={formErrors.link?.message}
          />
          <Input
            label="Where are you located? (City, Country)"
            name="location"
            error={formErrors.location?.message}
          />
          <Input
            label="Tell us a little about yourself and your business"
            name="message"
            error={formErrors.message?.message}
          />
        </Stack>
        <Stack space={4}>
          <Checkbox name="terms">
            <>
              I accept the{' '}
              <ControlLink to={hrefFor.TermsAndConditionsPage()}>Terms & Conditions</ControlLink>
            </>
          </Checkbox>
          <Checkbox name="subscribe">Sign me up for special access and updates</Checkbox>
          {error && <Text color="code-red">{error.message}</Text>}
        </Stack>
        <Button type="submit" className="ModaNetworkForm__submit">
          Submit
        </Button>
      </Stack>
      <Overlay show={loading}>
        <LoadingBalls />
        <Text treatment="eyebrow">Sending</Text>
      </Overlay>
    </form>
  );
};
